"use client";

import { useAppSelector, useAppDispatch } from "../../../../Redux/hooks";
import { useState, useEffect, useRef, useCallback } from "react";
import { clearSelectedStatusAd } from "../../../../Redux/E-commerce Ads Model/slice";
import styles from "./UpdateStatusMaxView.module.css";

const StatusAdMaxView = () => {
  const dispatch = useAppDispatch();
  const { selectedStatusAd, loading } = useAppSelector((state) => state.eCommerceAds);
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Just now";
    if (diffInHours === 1) return "1 hour ago";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 day ago";
    return `${diffInDays} days ago`;
  };

  const handleClose = () => {
    dispatch(clearSelectedStatusAd());
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsFullscreen(false);
        handleClose();
        break;
      case 'f':
      case 'F':
        e.preventDefault();
        setIsFullscreen((prev) => !prev);
        break;
      case 'p':
      case 'P':
        e.preventDefault();
        setIsPlaying((prev) => {
          const newValue = !prev;
          if (videoRef.current) {
            if (newValue) {
              videoRef.current.play().catch(console.error);
            } else {
              videoRef.current.pause();
            }
          }
          return newValue;
        });
        break;
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play().catch(console.error);
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  const isVideo = (url: string): boolean => {
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url) || 
           url.includes('video') || 
           (selectedStatusAd?.advertisementType?.toLowerCase().includes('video') ?? false);
  };

  const isImage = (url: string): boolean => {
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url) || 
           (selectedStatusAd?.advertisementType?.toLowerCase().includes('image') ?? false);
  };

  if (loading.statusAds) {
    return (
      <div className="d-flex justify-content-center align-items-center min-height-600">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading ad...</span>
        </div>
      </div>
    );
  }

  if (!selectedStatusAd) {
    return (
      <div className="d-flex justify-content-center align-items-center min-height-600">
        <div className="text-center">
          <h5>No ad selected</h5>
          <p className="text-muted">Select a status ad to view its details</p>
        </div>
      </div>
    );
  }

  const mediaUrl = selectedStatusAd.advertisementDurationandPricing?.advertisementMediaUrls || "";
  const isVideoType = isVideo(mediaUrl);
  const isImageType = isImage(mediaUrl);

  return (
    <div 
      className={`${styles['stories-viewer']} ${isFullscreen ? styles.fullscreen : ''}`}
      tabIndex={0}
      role="application"
      aria-label="Status ad viewer"
    >
      <div className={styles['story-container']}>
        {/* Header with ad info and controls */}
        <div className={styles['story-header']}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <button 
                className="btn btn-link text-white p-0"
                onClick={handleClose}
                style={{ textDecoration: 'none' }}
                aria-label="Close ad viewer"
              >
                <i className="ri-arrow-left-s-line fs-3"></i>
              </button>
              <div className="d-flex align-items-center gap-2">
                <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center"
                     style={{ width: '32px', height: '32px' }}>
                  <i className="bi bi-megaphone-fill text-white"></i>
                </div>
                <div>
                  <div className="text-white fw-bold" style={{ fontSize: '14px' }}>
                    {selectedStatusAd.advertisementTitle || 'Status Ad'}
                    <i className="bi bi-patch-check-fill text-info ms-1"></i>
                  </div>
                  {selectedStatusAd.createdAt && (
                    <div className="text-white-50" style={{ fontSize: '12px' }}>
                      {formatTimeAgo(selectedStatusAd.createdAt)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              {isVideoType && (
                <button 
                  className="btn btn-link text-white p-1"
                  onClick={() => setIsPlaying(!isPlaying)}
                  style={{ textDecoration: 'none' }}
                  aria-label={isPlaying ? 'Pause ad' : 'Play ad'}
                >
                  <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'} fs-5`}></i>
                </button>
              )}
              <button 
                className="btn btn-link text-white p-1"
                onClick={() => setIsFullscreen(!isFullscreen)}
                style={{ textDecoration: 'none' }}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                <i className="bi bi-arrows-fullscreen fs-5"></i>
              </button>
              <div className="dropdown">
                <button 
                  className="btn btn-link text-white p-1"
                  data-bs-toggle="dropdown"
                  style={{ textDecoration: 'none' }}
                  aria-label="Ad options"
                >
                  <i className="bi bi-three-dots fs-5"></i>
                </button>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="#">Share</a></li>
                  <li><a className="dropdown-item" href="#">Report</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Ad content */}
        <div 
          className={styles['story-content']}
          style={{ 
            height: isFullscreen ? '100vh' : '600px'
          }}
        >
          <div className={styles['story-media']}>
            {isVideoType ? (
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover' as const,
                  display: 'block'
                }}
                autoPlay={isPlaying}
                muted
                loop
                playsInline
                controls={false}
                onLoadedData={() => {
                  if (videoRef.current && isPlaying) {
                    videoRef.current.play().catch(console.error);
                  }
                }}
              >
                <source src={mediaUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : isImageType ? (
              <img
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover' as const,
                  display: 'block'
                }}
                src={mediaUrl || "../../assets/img/profile/profile-3.jpg"}
                alt={selectedStatusAd.advertisementTitle}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "../../assets/img/profile/profile-3.jpg";
                }}
              />
            ) : (
              <div 
                className={styles['text-story']}
                style={{ 
                  backgroundColor: "#000000",
                  color: "#ffffff"
                }}
              >
                <div className="text-center p-4">
                  <h2 className="fw-bold mb-3" style={{ fontSize: isFullscreen ? '2rem' : '1.5rem' }}>
                    {selectedStatusAd.advertisementTitle}
                  </h2>
                  {selectedStatusAd.advertisementDescription && (
                    <p style={{ fontSize: isFullscreen ? '1.2rem' : '1rem', maxWidth: '80%', margin: '0 auto' }}>
                      {selectedStatusAd.advertisementDescription}
                    </p>
                  )}
                  {selectedStatusAd.advertisementDurationandPricing?.cta && (
                    <a 
                      href={selectedStatusAd.advertisementDurationandPricing.ctaLink || '#'}
                      className="btn btn-primary mt-3"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedStatusAd.advertisementDurationandPricing.cta}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Ad details overlay */}
          {selectedStatusAd.advertisementDescription && (isVideoType || isImageType) && (
            <div className="position-absolute" style={{ bottom: '20px', left: '20px', right: '20px', zIndex: 10 }}>
              <div className=" bg-opacity-75 text-white p-3 rounded">
                
                {selectedStatusAd.advertisementDurationandPricing?.cta && (
                  <a 
                    href={selectedStatusAd.advertisementDurationandPricing.ctaLink || '#'}
                    className="btn btn-primary btn-sm"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedStatusAd.advertisementDurationandPricing.cta}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Ad stats badge */}
          <div className="position-absolute" style={{ bottom: '20px', right: '20px', zIndex: 10 }}>
            <div className="d-flex gap-2">
              <span className="badge bg-dark bg-opacity-50 text-white">
                <i className="bi bi-eye me-1"></i>
                {selectedStatusAd.reachedCount || 0}
              </span>
              <span className="badge bg-dark bg-opacity-50 text-white">
                <i className="bi bi-cursor me-1"></i>
                {selectedStatusAd.clickCount || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusAdMaxView;

